"use client"

import { motion } from "framer-motion"
import { Check, Star } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

interface ServicesFeaturesSectionProps {
  features: string[]
}

const clientPhotos=["/images/clients/clientsAnil.jpeg","/images/clients/Sugyani.jpeg","/images/clients/dipti.jpeg","/images/clients/sk.jpeg","/images/clients/sonalika.jpeg","/images/clients/Sunil.jpeg"]
export function ServicesFeaturesSection({ features }: ServicesFeaturesSectionProps) {
  return (
    <section className="py-20 sm:py-32 bg-gradient-to-br from-slate-50 via-blue-50/30 to-orange-50/20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#003DA5] rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF7A00] rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#003DA5]/10 to-[#FF7A00]/10 backdrop-blur-sm px-4 py-2 rounded-full border border-[#003DA5]/20 text-sm font-medium text-[#003DA5] mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Star className="w-4 h-4 text-[#FF7A00]" />
            Premium Service Features
          </motion.div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
            Why Choose Our{" "}
            <span className="bg-gradient-to-r from-[#003DA5] to-[#FF7A00] bg-clip-text text-transparent">
              Services
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Experience the difference with our comprehensive moving solutions designed around 
            your needs, backed by years of expertise and unwavering commitment to excellence
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -8 }}
            >
              {/* Card */}
              <div className="relative p-8 bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                {/* Gradient Border Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#003DA5]/20 to-[#FF7A00]/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
                
                {/* Icon Container */}
                <motion.div 
                  className="relative mb-6"
                  whileHover={{ rotate: 5, scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#003DA5] to-blue-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  {/* Floating accent */}
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-[#FF7A00] to-orange-600 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </motion.div>

                {/* Content */}
                <div className="space-y-3">
                  <p className="text-foreground font-semibold text-base leading-relaxed group-hover:text-[#003DA5] transition-colors duration-300">
                    {feature}
                  </p>
                  
                  {/* Animated underline */}
                  <div className="w-0 h-0.5 bg-gradient-to-r from-[#003DA5] to-[#FF7A00] group-hover:w-full transition-all duration-500"></div>
                </div>

                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#003DA5]/5 to-[#FF7A00]/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="inline-flex items-center gap-3 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full border border-white/50 shadow-lg">
            <div className="flex -space-x-2">
              {clientPhotos.map((photo) => (
                <Avatar key={photo} className="w-8 h-8 border-2 border-white">
                   <AvatarImage src={photo} alt="@shadcn" />
                </Avatar>
              ))}
            </div>
            <div className="text-sm">
              <span className="font-semibold text-foreground">500+ Happy Customers</span>
              <span className="text-muted-foreground"> trust our services</span>
            </div>
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className="w-4 h-4 text-[#FF7A00] fill-current" />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}